<template>
    <div class="tw-grid tw-grid-cols-12 tw-gap-4">
        <div class="tw-col-span-12 lg:tw-col-span-9">
            <div class="tw-w-full tw-h-full tw-bg-white tw-rounded-lg tw-p-4 tw-border tw-border-solid tw-border-primary-200 tw-min-h-[600px]">
                <!-- Header Section -->
                <div class="tw-flex tw-flex-col sm:tw-flex-row tw-items-start sm:tw-items-center tw-justify-between tw-gap-4 tw-mb-6">
                    <div>
                        <h1 class="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-1">Documents</h1>
                        
                        <p class="tw-text-sm tw-text-gray-600">{{ totalDocuments }} documents total</p>
                    </div>
                    
                    <!-- Header Actions -->
                    <div class="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
                        <!-- Search -->
                        <div class="tw-min-w-[250px]">
                            <FormField
                                v-model="searchQuery"
                                type="text"
                                placeholder="Search documents..."
                                left-icon="solar:magnifer-linear"
                                hide-label
                                hide-errors
                                class="tw-w-full"
                                @keyup.enter="performSearch"
                                @input="debounceSearch"
                                :disabled="loading"
                            />
                        </div>
                        
                        <!-- View Toggle -->
                        <div class="tw-flex tw-items-center tw-bg-gray-100 tw-rounded-md tw-p-1">
                            <button
                                @click="viewMode = 'grid'"
                                :disabled="loading"
                                :class="[
                                    'tw-px-3 tw-py-1.5 tw-rounded tw-text-sm tw-transition-colors',
                                    viewMode === 'grid' ? 'tw-bg-white tw-text-gray-900 tw-shadow-sm' : 'tw-text-gray-600 hover:tw-text-gray-900',
                                    loading ? 'tw-opacity-50 tw-cursor-not-allowed' : ''
                                ]"
                            >
                                <icon icon="solar:widget-4-bold-duotone" class="tw-text-lg" />
                            </button>
                            <button
                                @click="viewMode = 'list'"
                                :disabled="loading"
                                :class="[
                                    'tw-px-3 tw-py-1.5 tw-rounded tw-text-sm tw-transition-colors',
                                    viewMode === 'list' ? 'tw-bg-white tw-text-gray-900 tw-shadow-sm' : 'tw-text-gray-600 hover:tw-text-gray-900',
                                    loading ? 'tw-opacity-50 tw-cursor-not-allowed' : ''
                                ]"
                            >
                                <icon icon="solar:list-bold-duotone" class="tw-text-lg" />
                            </button>
                        </div>
                        
                        <!-- Upload Button -->
                        <a-button
                            @click="uploadModalVisible = true"
                            intent="gradient"
                            left-icon="solar:upload-bold-duotone"
                            :disabled="loading"
                        >
                            Upload
                        </a-button>
                        
                    </div>
                </div>

                <!-- Filters Bar -->
                <div v-if="false" class="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-mb-6 tw-p-4 tw-bg-gray-50 tw-rounded-lg">
                    <!-- Document Type Filter -->
                    <FormField
                        v-model="filters.type"
                        type="select"
                        hide-label
                        hide-errors
                        left-icon="solar:document-text-linear"
                    >
                        <option value="">All Types</option>
                        <option value="pdf">PDF</option>
                        <option value="doc">Documents</option>
                        <option value="image">Images</option>
                        <option value="spreadsheet">Spreadsheets</option>
                        <option value="other">Other</option>
                    </FormField>

                    <!-- Date Filter -->
                    <FormField
                        v-model="filters.date"
                        type="select"
                        hide-label
                        hide-errors
                        left-icon="solar:calendar-linear"
                    >
                        <option value="">All Dates</option>
                        <option value="today">Today</option>
                        <option value="week">This Week</option>
                        <option value="month">This Month</option>
                        <option value="year">This Year</option>
                    </FormField>

                    <!-- Sort -->
                    <FormField
                        v-model="sortBy"
                        type="select"
                        hide-label
                        hide-errors
                        left-icon="solar:sort-horizontal-linear"
                    >
                        <option value="name">Sort by Name</option>
                        <option value="date">Sort by Date</option>
                        <option value="size">Sort by Size</option>
                        <option value="type">Sort by Type</option>
                    </FormField>

                    <!-- Clear Filters -->
                    <a-button
                        v-if="hasActiveFilters"
                        @click="clearFilters"
                        intent="ghost"
                        left-icon="solar:refresh-bold-duotone"
                        class="tw-text-sm"
                    >
                        Clear
                    </a-button>

                    <!-- Bulk Actions -->
                    <div v-if="selectedDocuments.length > 0" class="tw-flex tw-items-center tw-gap-2 tw-ml-auto">
                        <span class="tw-text-sm tw-text-gray-600">{{ selectedDocuments.length }} selected</span>
                        <a-button
                            @click="downloadSelected"
                            intent="secondary"
                            left-icon="solar:download-bold-duotone"
                            class="tw-text-sm"
                        >
                            Download
                        </a-button>
                        <a-button
                            @click="deleteSelected"
                            intent="danger"
                            left-icon="solar:trash-bin-2-bold-duotone"
                            class="tw-text-sm"
                        >
                            Delete
                        </a-button>
                    </div>
                </div>

                <!-- Content Area -->
                <div class="tw-flex-1">
                    <!-- Loading State -->
                    <div v-if="loading">
                        <!-- Grid View Skeleton -->
                        <div v-if="viewMode === 'grid'" class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-3 tw-gap-4">
                            <div
                                v-for="n in 6"
                                :key="n"
                                class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-4 tw-animate-pulse"
                            >
                                <!-- Skeleton Checkbox and Actions -->
                                <div class="tw-flex tw-items-start tw-justify-between tw-mb-3">
                                    <div class="tw-w-4 tw-h-4 tw-bg-gray-200 tw-rounded"></div>
                                    <div class="tw-flex tw-items-center tw-gap-1">
                                        <div class="tw-w-8 tw-h-8 tw-bg-gray-200 tw-rounded-md"></div>
                                        <div class="tw-w-8 tw-h-8 tw-bg-gray-200 tw-rounded-md"></div>
                                        <div class="tw-w-8 tw-h-8 tw-bg-gray-200 tw-rounded-md"></div>
                                    </div>
                                </div>

                                <!-- Skeleton File Preview -->
                                <div class="tw-w-full tw-h-32 tw-bg-gray-200 tw-rounded-lg tw-mb-3"></div>

                                <!-- Skeleton File Info -->
                                <div>
                                    <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-mb-2 tw-w-3/4"></div>
                                    <div class="tw-flex tw-items-center tw-justify-between tw-mb-2">
                                        <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-12"></div>
                                        <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-16"></div>
                                    </div>
                                    <div class="tw-h-5 tw-bg-gray-200 tw-rounded tw-w-16"></div>
                                </div>
                            </div>
                        </div>

                        <!-- List View Skeleton -->
                        <div v-else class="tw-overflow-x-auto tw-border tw-border-gray-200 tw-rounded-lg">
                            <table class="tw-min-w-full tw-divide-y tw-divide-gray-200">
                                <thead class="tw-bg-gray-50">
                                    <tr>
                                        <th class="tw-px-4 tw-py-3 tw-text-left">
                                            <div class="tw-w-4 tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                                        </th>
                                        <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Name</th>
                                        <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Type</th>
                                        <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Size</th>
                                        <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Modified</th>
                                        <th class="tw-px-4 tw-py-3 tw-text-right tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody class="tw-bg-white tw-divide-y tw-divide-gray-200">
                                    <tr
                                        v-for="n in 8"
                                        :key="n"
                                        class="tw-animate-pulse"
                                    >
                                        <td class="tw-px-4 tw-py-4">
                                            <div class="tw-w-4 tw-h-4 tw-bg-gray-200 tw-rounded"></div>
                                        </td>
                                        <td class="tw-px-4 tw-py-4">
                                            <div class="tw-flex tw-items-center">
                                                <div class="tw-w-8 tw-h-8 tw-bg-gray-200 tw-rounded tw-mr-3"></div>
                                                <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-32"></div>
                                            </div>
                                        </td>
                                        <td class="tw-px-4 tw-py-4">
                                            <div class="tw-h-6 tw-bg-gray-200 tw-rounded-full tw-w-16"></div>
                                        </td>
                                        <td class="tw-px-4 tw-py-4">
                                            <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-12"></div>
                                        </td>
                                        <td class="tw-px-4 tw-py-4">
                                            <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-20"></div>
                                        </td>
                                        <td class="tw-px-4 tw-py-4 tw-text-right">
                                            <div class="tw-flex tw-items-center tw-justify-end tw-gap-2">
                                                <div class="tw-w-6 tw-h-6 tw-bg-gray-200 tw-rounded"></div>
                                                <div class="tw-w-6 tw-h-6 tw-bg-gray-200 tw-rounded"></div>
                                                <div class="tw-w-6 tw-h-6 tw-bg-gray-200 tw-rounded"></div>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <!-- Empty State -->
                    <div v-else-if="filteredDocuments.length === 0 && !searchQuery && !hasActiveFilters" class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-64 tw-text-center">
                        <icon icon="solar:document-add-bold-duotone" class="tw-text-6xl tw-text-gray-300 tw-mb-4" />
                        <h3 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-2">No documents yet</h3>
                        <p class="tw-text-gray-600 tw-mb-4">Start by uploading your first document</p>
                        <a-button
                            @click="uploadModalVisible = true"
                            intent="primary"
                            left-icon="solar:upload-bold-duotone"
                        >
                            Upload Document
                        </a-button>
                    </div>

                    <!-- No Search Results -->
                    <div v-else-if="filteredDocuments.length === 0" class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-64 tw-text-center">
                        <icon icon="solar:document-text-bold-duotone" class="tw-text-6xl tw-text-gray-300 tw-mb-4" />
                        <h3 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-2">No documents found</h3>
                        <p class="tw-text-gray-600 tw-mb-4">Try adjusting your search or filters</p>
                        <a-button
                            @click="clearFilters"
                            intent="secondary"
                            left-icon="solar:refresh-bold-duotone"
                        >
                            Clear Filters
                        </a-button>
                    </div>

                    <!-- Grid View -->
                    <div v-else-if="viewMode === 'grid'" class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-3 tw-gap-4">
                        <div
                            v-for="doc in paginatedDocuments"
                            :key="doc.id"
                            class="tw-group tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-4 hover:tw-shadow-md tw-transition-all tw-duration-200 tw-cursor-pointer"
                            @click="selectDocument(doc)"
                        >
                            <!-- Selection Checkbox -->
                            <div class="tw-flex tw-items-start tw-justify-between tw-mb-3">
                                <input
                                    type="checkbox"
                                    :checked="selectedDocuments.includes(doc.id)"
                                    @click.stop="toggleDocumentSelection(doc.id)"
                                    class="tw-w-4 tw-h-4 tw-text-primary-600 tw-border-gray-300 tw-rounded focus:tw-ring-primary-500"
                                />
                                <div class="tw-flex tw-items-center tw-gap-1 tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity">
                                    <button
                                        @click.stop="previewDocument(doc)"
                                        class="tw-p-1.5 tw-text-gray-400 hover:tw-text-gray-600 tw-rounded-md hover:tw-bg-gray-100"
                                        title="Preview"
                                    >
                                        <icon icon="solar:eye-bold-duotone" class="tw-text-lg" />
                                    </button>
                                    <button
                                        @click.stop="editDocument(doc)"
                                        class="tw-p-1.5 tw-text-gray-400 hover:tw-text-blue-600 tw-rounded-md hover:tw-bg-gray-100"
                                        title="Edit"
                                    >
                                        <icon icon="solar:pen-bold-duotone" class="tw-text-lg" />
                                    </button>
                                    <button
                                        @click.stop="downloadDocument(doc)"
                                        class="tw-p-1.5 tw-text-gray-400 hover:tw-text-gray-600 tw-rounded-md hover:tw-bg-gray-100"
                                        title="Download"
                                    >
                                        <icon icon="solar:download-bold-duotone" class="tw-text-lg" />
                                    </button>
                                    <button
                                        @click.stop="deleteDocument(doc)"
                                        class="tw-p-1.5 tw-text-gray-400 hover:tw-text-red-600 tw-rounded-md hover:tw-bg-gray-100"
                                        title="Delete"
                                    >
                                        <icon icon="solar:trash-bin-2-bold-duotone" class="tw-text-lg" />
                                    </button>
                                </div>
                            </div>

                            <!-- File Preview -->
                            <div class="tw-flex tw-items-center tw-justify-center tw-h-32 tw-bg-gray-50 tw-rounded-lg tw-mb-3">
                                <img
                                    v-if="doc.thumbnail_url"
                                    :src="doc.thumbnail_url"
                                    :alt="doc.name"
                                    class="tw-max-w-full tw-max-h-full tw-object-contain tw-rounded"
                                />
                                <icon
                                    v-else
                                    :icon="getFileIcon(doc.type)"
                                    class="tw-text-4xl tw-text-gray-400"
                                />
                            </div>

                            <!-- File Info -->
                            <div>
                                <h4 class="tw-font-medium tw-text-gray-900 tw-truncate tw-mb-1" :title="doc.name">{{ doc.name }}</h4>
                                <div class="tw-flex tw-items-center tw-justify-between tw-text-xs tw-text-gray-500">
                                    <span>{{ formatFileSize(doc.size) }}</span>
                                    <span>{{ formatDate(doc.created_at) }}</span>
                                </div>
                                <div class="tw-flex tw-items-center tw-gap-1 tw-mt-2">
                                    <span class="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-font-medium tw-bg-gray-100 tw-text-gray-800">
                                        {{ doc.type.toUpperCase() }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- List View -->
                    <div v-else class="tw-overflow-x-auto tw-border tw-border-gray-200 tw-rounded-lg">
                        <table class="tw-min-w-full tw-divide-y tw-divide-gray-200">
                            <thead class="tw-bg-gray-50">
                                <tr>
                                    <th class="tw-px-4 tw-py-3 tw-text-left">
                                        <input
                                            type="checkbox"
                                            :checked="allDocumentsSelected"
                                            @change="toggleAllDocuments"
                                            class="tw-w-4 tw-h-4 tw-text-primary-600 tw-border-gray-300 tw-rounded focus:tw-ring-primary-500"
                                        />
                                    </th>
                                    <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Name</th>
                                    <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Type</th>
                                    <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Size</th>
                                    <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Modified</th>
                                    <th class="tw-px-4 tw-py-3 tw-text-right tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody class="tw-bg-white tw-divide-y tw-divide-gray-200">
                                <tr
                                    v-for="doc in paginatedDocuments"
                                    :key="doc.id"
                                    class="hover:tw-bg-gray-50"
                                >
                                    <td class="tw-px-4 tw-py-4">
                                        <input
                                            type="checkbox"
                                            :checked="selectedDocuments.includes(doc.id)"
                                            @change="toggleDocumentSelection(doc.id)"
                                            class="tw-w-4 tw-h-4 tw-text-primary-600 tw-border-gray-300 tw-rounded focus:tw-ring-primary-500"
                                        />
                                    </td>
                                    <td class="tw-px-4 tw-py-4">
                                        <div class="tw-flex tw-items-center">
                                            <icon :icon="getFileIcon(doc.type)" class="tw-text-2xl tw-text-gray-400 tw-mr-3 tw-flex-shrink-0" />
                                            <div>
                                                <div class="tw-text-sm tw-font-medium tw-text-gray-900">{{ doc.name }}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="tw-px-4 tw-py-4">
                                        <span class="tw-inline-flex tw-items-center tw-px-2.5 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium tw-bg-gray-100 tw-text-gray-800">
                                            {{ doc.type.toUpperCase() }}
                                        </span>
                                    </td>
                                    <td class="tw-px-4 tw-py-4 tw-text-sm tw-text-gray-500">{{ formatFileSize(doc.size) }}</td>
                                    <td class="tw-px-4 tw-py-4 tw-text-sm tw-text-gray-500">{{ formatDate(doc.created_at) }}</td>
                                    <td class="tw-px-4 tw-py-4 tw-text-right tw-text-sm tw-font-medium">
                                        <div class="tw-flex tw-items-center tw-justify-end tw-gap-2">
                                            <button
                                                @click="previewDocument(doc)"
                                                class="tw-text-gray-400 hover:tw-text-gray-600"
                                                title="Preview"
                                            >
                                                <icon icon="solar:eye-bold-duotone" class="tw-text-lg" />
                                            </button>
                                            <button
                                                @click="editDocument(doc)"
                                                class="tw-text-gray-400 hover:tw-text-blue-600"
                                                title="Edit"
                                            >
                                                <icon icon="solar:pen-bold-duotone" class="tw-text-lg" />
                                            </button>
                                            <button
                                                @click="downloadDocument(doc)"
                                                class="tw-text-gray-400 hover:tw-text-gray-600"
                                                title="Download"
                                            >
                                                <icon icon="solar:download-bold-duotone" class="tw-text-lg" />
                                            </button>
                                            <button
                                                @click="deleteDocument(doc)"
                                                class="tw-text-gray-400 hover:tw-text-red-600"
                                                title="Delete"
                                            >
                                                <icon icon="solar:trash-bin-2-bold-duotone" class="tw-text-lg" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Pagination -->
                    <div v-if="totalPages > 1" class="tw-flex tw-items-center tw-justify-between tw-mt-6">
                        <div class="tw-text-sm tw-text-gray-600">
                            Showing {{ paginationMeta.from || 0 }} to {{ paginationMeta.to || 0 }} of {{ totalDocuments }} documents
                        </div>
                        <div class="tw-flex tw-items-center tw-gap-2">
                            <a-button
                                @click="goToPreviousPage"
                                :disabled="currentPage === 1"
                                intent="light"
                                left-icon="solar:arrow-left-linear"
                            >
                                Previous
                            </a-button>
                            <a-button
                                @click="goToNextPage"
                                :disabled="currentPage === totalPages"
                                intent="light"
                                right-icon="solar:arrow-right-linear"
                            >
                                Next
                            </a-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="tw-col-span-12 lg:tw-col-span-3">
            <div class="tw-w-full tw-h-fit tw-bg-white tw-rounded-lg tw-p-4 tw-pb-6 tw-border tw-border-solid tw-border-primary-200">
                <!-- Loading Stats Widget -->
                <div v-if="statsLoading">
                    <!-- Stats Widget Header Skeleton -->
                    <div class="tw-flex tw-items-center tw-justify-between tw-mb-4">
                        <div class="tw-h-6 tw-bg-gray-200 tw-rounded tw-w-24 tw-animate-pulse"></div>
                    </div>

                    <!-- Stats Items Skeleton -->
                    <div class="tw-space-y-4">
                        <!-- Skeleton Stat Item 1 -->
                        <div class="tw-flex tw-items-center tw-gap-3 tw-animate-pulse">
                            <div class="tw-w-10 tw-h-10 tw-bg-gray-200 tw-rounded-full tw-flex-shrink-0"></div>
                            <div class="tw-flex-1">
                                <div class="tw-h-6 tw-bg-gray-200 tw-rounded tw-w-16 tw-mb-1"></div>
                                <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-20"></div>
                            </div>
                        </div>

                        <!-- Skeleton Stat Item 2 -->
                        <div class="tw-flex tw-items-center tw-gap-3 tw-animate-pulse">
                            <div class="tw-w-10 tw-h-10 tw-bg-gray-200 tw-rounded-full tw-flex-shrink-0"></div>
                            <div class="tw-flex-1">
                                <div class="tw-h-6 tw-bg-gray-200 tw-rounded tw-w-12 tw-mb-1"></div>
                                <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-28"></div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Actual Stats Widget -->
                <div v-else>
                    <!-- Stats Widget Header -->
                    <div class="tw-flex tw-items-center tw-justify-between tw-mb-4">
                        <h2 class="tw-text-lg tw-font-semibold tw-text-primary-900">Your stats</h2>
                        <button v-if="false" class="tw-p-1 tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors tw-duration-200">
                            <icon icon="solar:filter-linear" class="tw-text-lg" />
                        </button>
                    </div>

                    <!-- Stats Items -->
                    <div class="tw-space-y-4">
                        <!-- Total Views Stat -->
                        <div class="tw-flex tw-items-center tw-gap-3">
                            <div class="tw-w-10 tw-h-10 tw-bg-gray-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                <icon icon="solar:eye-linear" class="tw-text-lg tw-text-gray-600" />
                            </div>
                            <div class="tw-flex-1">
                                <div class="tw-text-xl tw-font-bold tw-text-gray-900">{{ stats.totalViews }}</div>
                                <div class="tw-text-sm tw-text-gray-500">total views</div>
                            </div>
                        </div>

                        <!-- Total Downloads Stat -->
                        <div class="tw-flex tw-items-center tw-gap-3">
                            <div class="tw-w-10 tw-h-10 tw-bg-gray-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                <icon icon="solar:download-outline" class="tw-text-lg tw-text-gray-600" />
                            </div>
                            <div class="tw-flex-1">
                                <div class="tw-text-xl tw-font-bold tw-text-gray-900">{{ stats.totalDownloads }}</div>
                                <div class="tw-text-sm tw-text-gray-500">total downloads</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Upload Modal -->
        <DocumentUploadModal
            :visible="uploadModalVisible"
            @close="uploadModalVisible = false"
            @uploaded="handleDocumentUploaded"
        />

        <!-- Edit Modal -->
        <DocumentEditModal
            :visible="editModalVisible"
            :document="documentToEdit"
            @close="editModalVisible = false"
            @updated="handleDocumentUpdated"
        />

        <!-- Delete Confirmation Modal -->
        <DeleteModal
            :visible="deleteModalVisible"
            :loading="deletingDocument"
            @confirm="confirmDelete"
            @cancel="deleteModalVisible = false"
            :title="deleteModalTitle"
            :description="deleteModalDescription"
        />
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import DeleteModal from '@/components/modals/DeleteModal.vue'
import DocumentUploadModal from '@/components/modals/DocumentUploadModal.vue'
import DocumentEditModal from '@/components/modals/DocumentEditModal.vue'
import FormField from '@/components/form/FormField.vue'
import { paginate as paginateDocuments, destroy as destroyDocument } from '@/api/documents'
import useAlert from '@/composables/useAlert'

// Data
const loading = ref(false)
const statsLoading = ref(false)
const searchQuery = ref('')
const viewMode = ref('grid')
const currentPage = ref(1)
const itemsPerPage = ref(12)
const selectedDocuments = ref([])
const uploadModalVisible = ref(false)
const editModalVisible = ref(false)
const deleteModalVisible = ref(false)
const deletingDocument = ref(false)
const documentToDelete = ref(null)
const documentToEdit = ref(null)

// Pagination data from server
const totalDocuments = ref(0)
const serverTotalPages = ref(0)
const paginationMeta = ref({})

// Filters
const filters = ref({
    type: '',
    date: ''
})
const sortBy = ref('name')



// Stats data
const stats = ref({
    totalViews: '500k',
    totalDownloads: '20k'
})

// Documents data
const documents = ref([])

// Computed properties
const hasActiveFilters = computed(() => {
    return filters.value.type || filters.value.date || searchQuery.value
})

// With server-side pagination, we use the documents directly
const paginatedDocuments = computed(() => {
    return documents.value
})

// Use server-provided total pages
const totalPages = computed(() => {
    return serverTotalPages.value
})

// For display purposes
const filteredDocuments = computed(() => {
    return documents.value
})

const allDocumentsSelected = computed(() => {
    return paginatedDocuments.value.length > 0 && 
           paginatedDocuments.value.every(doc => selectedDocuments.value.includes(doc.id))
})

const deleteModalTitle = computed(() => {
    if (selectedDocuments.value.length > 1) {
        return `Delete ${selectedDocuments.value.length} documents?`
    }
    return 'Delete document?'
})

const deleteModalDescription = computed(() => {
    if (selectedDocuments.value.length > 1) {
        return 'This action cannot be undone. All selected documents will be permanently deleted.'
    }
    return 'This action cannot be undone. The document will be permanently deleted.'
})

// Methods
const clearFilters = () => {
    filters.value.type = ''
    filters.value.date = ''
    searchQuery.value = ''
    currentPage.value = 1
    fetchDocuments() // Refetch with cleared filters
}

// Pagination methods
const goToPreviousPage = () => {
    if (currentPage.value > 1) {
        currentPage.value--
        fetchDocuments()
    }
}

const goToNextPage = () => {
    if (currentPage.value < totalPages.value) {
        currentPage.value++
        fetchDocuments()
    }
}

// Search functionality
const performSearch = () => {
    currentPage.value = 1 // Reset to first page when searching
    fetchDocuments()
}

// Debounced search
let searchTimeout = null
const debounceSearch = () => {
    clearTimeout(searchTimeout)
    searchTimeout = setTimeout(() => {
        performSearch()
    }, 500) // Wait 500ms after user stops typing
}

const toggleDocumentSelection = (documentId) => {
    const index = selectedDocuments.value.indexOf(documentId)
    if (index > -1) {
        selectedDocuments.value.splice(index, 1)
    } else {
        selectedDocuments.value.push(documentId)
    }
}

const toggleAllDocuments = () => {
    if (allDocumentsSelected.value) {
        selectedDocuments.value = selectedDocuments.value.filter(id =>
            !paginatedDocuments.value.some(doc => doc.id === id)
        )
    } else {
        paginatedDocuments.value.forEach(doc => {
            if (!selectedDocuments.value.includes(doc.id)) {
                selectedDocuments.value.push(doc.id)
            }
        })
    }
}

const selectDocument = (doc) => {
    // Handle document selection/preview
    previewDocument(doc)
}

const previewDocument = (doc) => {
    // Open document preview using file_url
    if (doc.file_url) {
        window.open(doc.file_url, '_blank')
    } else {
        useAlert('Document file not available', 'danger')
    }
}

const downloadDocument = (doc) => {
    // Download document using file_url
    if (doc.file_url) {
        const link = document.createElement('a')
        link.href = doc.file_url
        link.download = doc.name
        link.click()
    } else {
        useAlert('Document file not available', 'danger')
    }
}

const downloadSelected = () => {
    // Simulate bulk download
    console.log('Downloading selected documents:', selectedDocuments.value)
    selectedDocuments.value = []
}

const editDocument = (doc) => {
    documentToEdit.value = doc
    editModalVisible.value = true
}

const deleteDocument = (doc) => {
    documentToDelete.value = doc
    selectedDocuments.value = [doc.id]
    deleteModalVisible.value = true
}

const deleteSelected = () => {
    deleteModalVisible.value = true
}

const confirmDelete = async () => {
    deletingDocument.value = true
    
    try {
        // Delete all selected documents
        await Promise.all(
            selectedDocuments.value.map(docId => destroyDocument(docId))
        )
        
        // Remove deleted documents from local state
        documents.value = documents.value.filter(doc => 
            !selectedDocuments.value.includes(doc.id)
        )
        
        useAlert('Document(s) deleted successfully', 'success')
        selectedDocuments.value = []
        
    } catch (error) {
        console.error('Error deleting documents:', error)
        useAlert('Failed to delete document(s)', 'danger')
    } finally {
        deletingDocument.value = false
        deleteModalVisible.value = false
        documentToDelete.value = null
    }
}

const handleDocumentUploaded = () => {
    // Refresh the documents list to include the newly uploaded document
    fetchDocuments()
}

const handleDocumentUpdated = (updatedDoc) => {
    // Update the document in the local state
    const index = documents.value.findIndex(doc => doc.id === updatedDoc.id)
    if (index !== -1) {
        documents.value[index] = updatedDoc
    }
    // Alternatively, refresh the entire list to ensure consistency
    fetchDocuments()
}

// Fetch documents from API with pagination
const fetchDocuments = async () => {
    loading.value = true
    try {
        const params = {
            page: currentPage.value,
            per_page: itemsPerPage.value,
            search: searchQuery.value || undefined,
            type: filters.value.type || undefined,
            status: undefined, // Could add status filter later
        }
        
        const response = await paginateDocuments(params)
        
        // Handle Laravel pagination response
        documents.value = response.data.data || []
        totalDocuments.value = response.data.total || 0
        serverTotalPages.value = response.data.last_page || 0
        paginationMeta.value = response.data
        
    } catch (error) {
        console.error('Error fetching documents:', error)
        useAlert('Failed to load documents', 'danger')
        documents.value = []
        totalDocuments.value = 0
        serverTotalPages.value = 0
    } finally {
        loading.value = false
    }
}

// Fetch stats
const fetchStats = async () => {
    statsLoading.value = true
    try {
        // For now, use static stats - could be enhanced to use real analytics
        stats.value = {
            totalViews: '500k',  // This would come from analytics API
            totalDownloads: '20k'  // This would come from analytics API
        }
    } catch (error) {
        console.error('Error fetching stats:', error)
    } finally {
        statsLoading.value = false
    }
}

const getFileIcon = (type) => {
    const iconMap = {
        pdf: 'solar:document-text-bold-duotone',
        doc: 'solar:document-bold-duotone',
        image: 'solar:image-bold-duotone',
        spreadsheet: 'solar:chart-square-bold-duotone',
        other: 'solar:file-bold-duotone'
    }
    return iconMap[type] || iconMap.other
}

const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes'
    
    // Convert string to number if needed
    const size = typeof bytes === 'string' ? parseInt(bytes) : bytes
    if (isNaN(size)) return '0 Bytes'
    
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(size) / Math.log(k))
    return parseFloat((size / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    })
}

// Initialize component
const initializeComponent = async () => {
    // Start both loading states
    loading.value = true
    statsLoading.value = true
    
    try {
        // Fetch documents first
        await fetchDocuments()
        
        // Then fetch stats (which might depend on documents data)
        await fetchStats()
    } catch (error) {
        console.error('Error initializing component:', error)
    }
}

onMounted(() => {
    initializeComponent()
})
</script>

<style scoped>

</style>